import type { InputHTMLAttributes, Ref } from 'react';

import { cx } from './cx.js';

type Native = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'size' | 'type' | 'className'
>;

export type FieldProps = Native & {
  value: string;
  onChange: (value: string) => void;
  /**
   * The accessible name, and the placeholder unless `placeholder` overrides
   * it. Required — a text box with no name is unusable with a screen reader
   * and ambiguous with one.
   */
  label: string;
  placeholder?: string;
  /** `md` is the default 48px. `sm` is 44px — the floor, never below it. */
  size?: 'md' | 'sm';
  /** Digits only. Brings up the numeric keypad and stops autocorrect. */
  numeric?: boolean;
  /** A date picker rather than a text box. Same metrics, same ring. */
  date?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
};

/**
 * The only text input in the app.
 *
 * Before this existed every screen hand-rolled `<input className="h-11
 * rounded-lg border border-line bg-sunken …">`, and four different heights
 * were in use at once — which is the whole reason the inputs felt like they
 * came from different products. Anything that needs a text box goes through
 * here now, so the metrics, the focus ring and the keyboard behaviour are
 * decided once.
 *
 * Three things it does that the hand-rolled versions mostly did not:
 *
 * **It is never smaller than 44px.** The plan slots were 36px, and they are
 * the fields we most want a tired student to hit on the first try.
 *
 * **It tells the phone what keyboard to show.** `inputMode`, `enterKeyHint`
 * and `autocapitalize` are the difference between typing a number on a
 * numeric pad and hunting for it on a QWERTY one.
 *
 * **It scrolls itself clear of the keyboard.** On a small Android phone the
 * on-screen keyboard covers the bottom half of the viewport, and the field
 * you just tapped is often under it. Nothing in the app handled that.
 */
export function Field({
  value,
  onChange,
  label,
  placeholder,
  size = 'md',
  numeric = false,
  date = false,
  inputRef,
  className,
  onFocus,
  ...rest
}: FieldProps) {
  return (
    <input
      ref={inputRef}
      type={date ? 'date' : 'text'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder ?? label}
      aria-label={label}
      // A name field holds a person's own words — "Read physics", "আম্মুকে
      // ফোন" — so autocomplete has nothing useful to offer and only gets in
      // the way. Numbers get the keypad; text keeps sentence capitals.
      autoComplete="off"
      {...(numeric
        ? { inputMode: 'numeric' as const, autoCapitalize: 'off', spellCheck: false }
        : { autoCapitalize: 'sentences' })}
      // Every field in this app is a short answer, and every one of them is
      // submitted by Enter. "Done" is what the key should say.
      enterKeyHint="done"
      onFocus={(event) => {
        // After the frame, so the keyboard has begun to appear and the
        // viewport has already shrunk. Optional-chained: jsdom has no layout
        // and implements neither.
        const element = event.currentTarget;
        requestAnimationFrame(() =>
          element.scrollIntoView?.({ block: 'center', behavior: 'smooth' }),
        );
        onFocus?.(event);
      }}
      className={cx(
        size === 'md' ? 'h-12 text-base' : 'h-11 text-sm',
        'min-w-0 rounded-lg border border-line bg-sunken px-3',
        'text-ink outline-none placeholder:text-subtle',
        'focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      {...rest}
    />
  );
}
